import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Spinner, Col, UncontrolledCollapse } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import useApi from 'hooks/useApi';
import { selectCurrentClient } from 'redux/actions/clientActions';
import { setCollapseClient } from 'redux/actions/notificationReviewActions';
import { setShowTypeNotifications } from 'redux/actions/notificationActions';

import { ReactComponent as ClientIcon } from 'assets/icon-consulting.svg';
import { ReactComponent as AngleArrowRightIcon } from 'assets/icon-angle-right.svg';
import { ReactComponent as SearchIcon } from 'assets/icon-search.svg';
import { ReactComponent as AngleArrowLeftIcon } from 'assets/icon-angle-left.svg';
import { ReactComponent as CaretDownIcon } from 'assets/icon-angle-down.svg';
import { ReactComponent as CaretUpIcon } from 'assets/icon-angle-up.svg';

import { colors } from 'shared/defaultTheme';

import {
  SearchDiv,
  SearchClients,
  HrClient,
  SearchClientsForm,
  Ul,
  CollapseClient,
  Individual,
  Massive,
} from './styles';

import { ClientPanelBackground, CollapseClientRight } from '../styles';

const Clients = () => {
  const { customAreaApi } = useApi();
  const dispatch = useDispatch();
  const theme = useSelector((state) => state.theme);
  const auth = useSelector((state) => state.auth);
  const { currentClient } = useSelector((state) => state.clientState);
  const { showTypeNotifications } = useSelector((state) => state.notification);
  const { isCollapse, notificationReview, currentNotificationReview } = useSelector(
    (state) => state.notificationReview
  );
  const [searchValue, setSearchValue] = useState('');
  const { t } = useTranslation();
  const { mainColor } = theme;
  const [clientList, setClientList] = useState([]);
  const [individualIsOpen, setIndividualIsOpen] = useState(false);

  const [{ loading }, getClientsData] = customAreaApi(
    {
      url: `${window.API_V1}/cli/clients/${auth.cusNavId}/search`,
      method: 'POST',
    },
    { manual: true }
  );

  const clientsData = async () => {
    try {
      const {
        data: { data },
      } = await getClientsData({
        data: {
          filters: [
            {
              model: 'Client',
              field: 'ClientId',
            },
          ],
          reduce: {
            limit: 0,
            skip: 0,
          },
        },
      });
      setClientList(data);
      const findClientId = data.find((x) => x.clientId === currentNotificationReview?.clientId);
      dispatch(selectCurrentClient(findClientId || data[0]));
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    clientsData();
  }, []);

  useEffect(() => {
    if (!showTypeNotifications) {
      setIndividualIsOpen(true);
    } else {
      setIndividualIsOpen(false);
    }
  }, [showTypeNotifications]);

  const searchClients = async (e) => {
    if (e) {
      const { value } = e.target;
      setSearchValue(value);
      try {
        const {
          data: { data },
        } = await getClientsData({
          data: {
            filters: [
              {
                model: 'Client',
                field: 'ClientName',
                filter: [value],
                isRegex: true,
              },
            ],
            reduce: {
              limit: 0,
              skip: 0,
            },
          },
        });
        setClientList(data);
      } catch (error) {
        setClientList([]);
      }
    } else {
      setSearchValue('');
      try {
        const {
          data: { data },
        } = await getClientsData({
          data: {
            filters: [
              {
                model: 'Client',
                field: 'ClientName',
                filter: [''],
                isRegex: true,
              },
            ],
            reduce: {
              limit: 0,
              skip: 0,
            },
          },
        });
        setClientList(data);
      } catch (error) {
        setClientList([]);
      }
    }
  };

  const onSelectClient = (client) => {
    dispatch(selectCurrentClient(client));
  };

  return (
    <>
      {!isCollapse ? (
        <ClientPanelBackground className="mr-4">
          <SearchDiv className="d-flex">
            <Col>
              <SearchClientsForm className="form">
                <SearchClients
                  Icon={() => null}
                  borderRadius={2}
                  className="mb-4 w-25 m-0 h-50"
                  inputClassName="w-100"
                  buttonClassName=""
                  withButtonClear
                  rightIcon={<SearchIcon fill={colors.black} />}
                  onChange={searchClients}
                  value={searchValue ?? ''}
                  placeholder={t('home_search_client')}
                />
              </SearchClientsForm>
            </Col>
            <CollapseClient onClick={() => dispatch(setCollapseClient(true))}>
              <AngleArrowLeftIcon />
            </CollapseClient>
          </SearchDiv>
          <HrClient />
          <Massive
            onClick={() => {
              dispatch(setShowTypeNotifications(true));
            }}
            showTypeNotifications={showTypeNotifications}
          >
            <span>{t('notifications_text_massive')}</span>
          </Massive>
          <Individual
            id="individual-toggler"
            onClick={() => {
              setIndividualIsOpen(!individualIsOpen);
              dispatch(setShowTypeNotifications(false));
            }}
            showTypeNotifications={showTypeNotifications}
          >
            <span>{t('notifications_text_individual')}</span>
            {individualIsOpen ? (
              <CaretUpIcon className="caret" width={15} />
            ) : (
              <CaretDownIcon className="caret" width={15} />
            )}
          </Individual>
          <UncontrolledCollapse toggler="#individual-toggler" isOpen={individualIsOpen}>
            <Ul className="list-unstyled mt-2" isCurrentNotification={notificationReview}>
              {!loading ? (
                <>
                  {clientList.map((client) => (
                    <Li
                      key={client.clientId}
                      onClick={() => onSelectClient(client)}
                      selectedColor={
                        currentClient?.clientId === client.clientId
                          ? colors.whiteSmoke
                          : colors.white
                      }
                    >
                      <Div>{client.clientName}</Div>
                    </Li>
                  ))}
                </>
              ) : (
                <div className="d-flex justify-content-center mb-5 align-items-center h-100">
                  <Spinner color={mainColor} />
                </div>
              )}
            </Ul>
            {!clientList.length && (
              <div className="d-flex flex-column justify-content-center align-items-center">
                <ClientIcon width={57} fill={colors.suvaGrey} height={65} className="mb-2" />
                <span>{t('client_not_found')}</span>
              </div>
            )}
          </UncontrolledCollapse>
        </ClientPanelBackground>
      ) : (
        <div className="collapse-client-container mr-3">
          <CollapseClientRight onClick={() => dispatch(setCollapseClient(false))}>
            <AngleArrowRightIcon />
          </CollapseClientRight>
        </div>
      )}
    </>
  );
};

const Div = styled.div`
  width: 100%;
  height: 45px;
  cursor: pointer;
  padding: 1rem 2rem;
`;

const Li = styled.li`
  background: ${({ selectedColor }) => selectedColor};
  &:hover {
    background: ${colors.whiteSmoke};
  }
`;

export default Clients;
